import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Verify from "@/pages/Verify";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

import Consent from "@/pages/patient/Consent";
import PatientDashboard from "@/pages/patient/PatientDashboard";
import VoiceCapture from "@/pages/patient/VoiceCapture";
import ClinicianDashboard from "@/pages/clinician/ClinicianDashboard";
import PatientProfile from "@/pages/clinician/PatientProfile";
import MappingConfig from "@/pages/shared/MappingConfig";
import LabDashboard from "@/pages/lab/LabDashboard";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAudit from "@/pages/admin/AdminAudit";

function RoleHome() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={`/${user.role}`} replace />;
}

function Shell({ children, roles }) {
  return (
    <ProtectedRoute roles={roles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify" element={<ProtectedRoute><Verify /></ProtectedRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/home" element={<RoleHome />} />

            {/* Patient */}
            <Route path="/patient" element={<Shell roles={["patient"]}><PatientDashboard /></Shell>} />
            <Route path="/patient/record" element={<Shell roles={["patient"]}><VoiceCapture /></Shell>} />
            <Route path="/patient/consent" element={<Shell roles={["patient"]}><Consent /></Shell>} />

            {/* Clinician */}
            <Route path="/clinician" element={<Shell roles={["clinician", "admin"]}><ClinicianDashboard /></Shell>} />
            <Route path="/clinician/patient/:id" element={<Shell roles={["clinician", "admin"]}><PatientProfile /></Shell>} />
            <Route path="/clinician/mapping" element={<Shell roles={["clinician", "admin"]}><MappingConfig /></Shell>} />

            {/* Lab */}
            <Route path="/lab" element={<Shell roles={["lab", "admin"]}><LabDashboard /></Shell>} />

            {/* Admin */}
            <Route path="/admin" element={<Shell roles={["admin"]}><AdminOverview /></Shell>} />
            <Route path="/admin/users" element={<Shell roles={["admin"]}><AdminUsers /></Shell>} />
            <Route path="/admin/mapping" element={<Shell roles={["admin"]}><MappingConfig /></Shell>} />
            <Route path="/admin/audit" element={<Shell roles={["admin"]}><AdminAudit /></Shell>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
